import "@tamagui/core/reset.css";
import "@/theme/styles.css";
import "@/theme/tamagui-web.css";

import { ToastProvider } from "@tamagui/toast";
import Head from "expo-router/head";
import {
  Theme as RNNTheme,
  ThemeProvider as RNNThemeProvider,
} from "expo-router/react-navigation";
import { useEffect } from "react";
import { TamaguiProvider, Theme, useTheme } from "tamagui";

import { brandName } from "@/config/brand";
import { setResolvedThemeSnapshot, useResolvedTheme } from "@/theme/helpers";
import { tamaguiConfig } from "../theme/tamagui.config";
/**
 * Dependent providers:
 * - FeatureFlagProvider
 * - ClerkProvider
 */
export default function ThemeProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const resolvedTheme = useResolvedTheme();

  // Imperative callers cannot read the feature flag hook, so we have to update the resolved theme.
  useEffect(() => {
    setResolvedThemeSnapshot(resolvedTheme);
  }, [resolvedTheme]);

  return (
    <TamaguiProvider config={tamaguiConfig} defaultTheme={resolvedTheme}>
      <Theme name={resolvedTheme}>
        <MetaReplacer />
        {/* Override the RNN theme to prevent leaking around the border radius of the Expo Router Stack */}
        <RNNThemeProvider value={{ colors: {} } as RNNTheme}>
          <ToastProvider duration={3000}>{children}</ToastProvider>
        </RNNThemeProvider>
      </Theme>
    </TamaguiProvider>
  );
}

const MetaReplacer = () => {
  const theme = useTheme();
  // +html.tsx creates meta tags for light and dark modes since we don't yet know the user's theme
  // Now that we do, we replace them with the user's preferred theme color
  useEffect(() => {
    if (typeof window === "undefined") return;
    const metas = document.getElementsByTagName("meta");
    for (let i = metas.length - 1; i >= 0; i--) {
      if (
        metas[i].getAttribute("name") === "theme-color" &&
        metas[i].getAttribute("media")
      ) {
        document.head.removeChild(metas[i]);
      }
    }
  }, []);
  return (
    <Head>
      <title>{brandName}</title>
      <meta name="theme-color" content={theme.backgroundGround?.val} />
    </Head>
  );
};
